import { useQuery } from '@apollo/client/react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GQL_PING } from '~/gql/common/common'
import { env } from '~/helpers/env'
import { cn } from '~/lib/utils'

function StatusRow({ label, ok, value }: { label: string, ok: boolean | null, value: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="inline-flex items-center gap-1.5 font-medium">
        <span
          className={cn(
            'size-1.5 rounded-full',
            ok === null ? 'animate-pulse bg-yellow-500' : ok ? 'bg-green-500' : 'bg-red-500',
          )}
        />
        {value}
      </span>
    </div>
  )
}

/**
 * Indicateur d'environnement discret (dev/staging uniquement) : une pastille en
 * bas à gauche qui déplie au survol le statut API/GraphQL/version. Masqué en prod.
 * Remplace l'ancien bandeau supérieur.
 */
export function DevStatusPill() {
  const { t } = useTranslation()
  const { data, loading } = useQuery(GQL_PING)
  const [apiOk, setApiOk] = useState<boolean | null>(null)

  useEffect(() => {
    let cancelled = false
    fetch(`${env.api.url}/`)
      .then(res => res.json())
      .then((json) => { if (!cancelled) setApiOk(json.status === 'ok') })
      .catch(() => { if (!cancelled) setApiOk(false) })
    return () => { cancelled = true }
  }, [])

  if (env.app.env === 'live') return null

  const gqlOk = loading ? null : !!data?.getHealthGql.time
  const apiVersion = data?.getHealthGql.version
  const versionOk = loading ? null : apiVersion === env.app.version
  const allOk = apiOk === true && gqlOk === true && versionOk === true
  const anyLoading = apiOk === null || loading

  return (
    <div className="group fixed bottom-3 left-3 z-50">
      {/* Détail au survol */}
      <div className="pointer-events-none absolute bottom-full left-0 mb-2 w-60 origin-bottom-left scale-95 rounded-lg border border-border bg-popover p-3 text-xs text-popover-foreground opacity-0 shadow-lg transition group-hover:pointer-events-auto group-hover:scale-100 group-hover:opacity-100">
        <div className="mb-2 flex items-center justify-between">
          <span className="font-semibold uppercase tracking-wide text-muted-foreground">{t('common.environment')}</span>
          <span className="rounded-full bg-muted px-2 py-0.5 font-medium text-primary-text">{env.app.env}</span>
        </div>
        <div className="flex flex-col gap-1.5">
          <StatusRow label="API" ok={apiOk} value={apiOk === null ? '…' : apiOk ? t('is.reachable') : t('is.not.reachable')} />
          <StatusRow label="GraphQL" ok={gqlOk} value={gqlOk === null ? '…' : gqlOk ? t('is.reachable') : t('is.not.reachable')} />
          <StatusRow
            label="Version"
            ok={versionOk}
            value={versionOk === false ? `web ${env.app.version} · api ${apiVersion ?? '—'}` : env.app.version}
          />
        </div>
      </div>

      {/* Pastille compacte */}
      <div
        className="flex cursor-default items-center gap-2 rounded-full border border-border bg-background/90 px-3 py-1.5 text-xs font-medium shadow-sm backdrop-blur"
        title={t('common.environment')}
      >
        <span
          className={cn(
            'size-2 rounded-full',
            anyLoading ? 'animate-pulse bg-yellow-500' : allOk ? 'bg-green-500' : 'bg-red-500',
          )}
        />
        {env.app.env}
      </div>
    </div>
  )
}
